import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays, ListChecks, Heart, Compass, CalendarRange,
  Check, X, ArrowRight, ArrowLeft, MessageSquare, Sparkles,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useAuth } from "@/auth/AuthContext";
import { useTranslation } from "@/i18n";

const TOTAL = 5;

// Lightweight, on-brand preview card wrapper (white, rounded, thin border, soft shadow).
function Preview({ children }) {
  return (
    <div className="rounded-[20px] bg-white border border-border shadow-sm p-4 sm:p-5" data-testid="onboarding-preview">
      {children}
    </div>
  );
}

function AiRow({ icon: Icon, name, desc }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-border bg-white px-3 py-2.5">
      <span className="mt-0.5 h-8 w-8 shrink-0 rounded-lg bg-slate-900 text-white flex items-center justify-center">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-900">{name}</p>
        <p className="text-xs text-slate-600 mt-0.5">{desc}</p>
      </div>
    </div>
  );
}

export default function Onboarding() {
  const { t } = useTranslation();
  const { user, completeOnboarding } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  const exit = async () => {
    await completeOnboarding();
    if (user && !user.startersCompleted) navigate("/starters", { replace: true });
    else navigate("/", { replace: true });
  };

  const next = () => (step < TOTAL - 1 ? setStep((s) => s + 1) : exit());
  const back = () => setStep((s) => Math.max(0, s - 1));

  const screens = [
    // Screen 1
    <div key="s1" className="space-y-5" data-testid="onboarding-screen-1">
      <Header title={t("onboarding.s1.title")} body={t("onboarding.s1.body")} />
      <Preview>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Chronicles</p>
            <p className="font-display text-lg font-semibold text-slate-900">14:32</p>
          </div>
          <span className="h-9 w-9 rounded-xl bg-slate-900 text-white flex items-center justify-center"><CalendarDays className="h-4 w-4" /></span>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {[["cal", CalendarDays], ["tasks", ListChecks], ["msg", MessageSquare], ["compass", Compass]].map(([k, I]) => (
            <div key={k} className="rounded-xl border border-border h-12 flex items-center justify-center text-slate-500"><I className="h-4 w-4" /></div>
          ))}
        </div>
      </Preview>
    </div>,

    // Screen 2
    <div key="s2" className="space-y-5" data-testid="onboarding-screen-2">
      <Header title={t("onboarding.s2.title")} body={t("onboarding.s2.body")} />
      <Preview>
        <div className="grid grid-cols-3 gap-2 text-center">
          {[
            { I: CalendarRange, k: "Calendar" },
            { I: ListChecks, k: "Tasks" },
            { I: Sparkles, k: "AI" },
          ].map(({ I, k }) => (
            <div key={k} className="rounded-xl border border-border py-4 flex flex-col items-center gap-1.5">
              <I className="h-5 w-5 text-slate-700" />
              <span className="text-[11px] text-slate-600">{k}</span>
            </div>
          ))}
        </div>
      </Preview>
    </div>,

    // Screen 3
    <div key="s3" className="space-y-5" data-testid="onboarding-screen-3">
      <Header title={t("onboarding.s3.title")} />
      <div className="space-y-2">
        <AiRow icon={CalendarRange} name={t("onboarding.s3.assistant")} desc={t("onboarding.s3.assistantDesc")} />
        <AiRow icon={Compass} name={t("onboarding.s3.strategy")} desc={t("onboarding.s3.strategyDesc")} />
        <AiRow icon={Heart} name={t("onboarding.s3.support")} desc={t("onboarding.s3.supportDesc")} />
      </div>
    </div>,

    // Screen 4
    <div key="s4" className="space-y-5" data-testid="onboarding-screen-4">
      <Header title={t("onboarding.s4.title")} />
      <ol className="space-y-2">
        {[t("onboarding.s4.step1"), t("onboarding.s4.step2"), t("onboarding.s4.step3")].map((s, i) => (
          <li key={s} className="flex items-center gap-3 rounded-xl border border-border bg-white px-3 py-2.5">
            <span className="h-6 w-6 shrink-0 rounded-full bg-slate-900 text-white text-xs flex items-center justify-center">{i + 1}</span>
            <span className="text-sm text-slate-800">{s}</span>
          </li>
        ))}
      </ol>
      <Preview>
        <div className="rounded-xl border-l-4 border-slate-300 bg-slate-50 px-3 py-2 flex items-center justify-between">
          <span className="text-sm font-medium text-slate-800">{t("onboarding.s4.chronicleLabel")}</span>
          <span className="text-xs font-mono-tab text-slate-500">09:00–10:00</span>
        </div>
        <div className="mt-2 rounded-xl border border-border px-3 py-2 flex items-center gap-2">
          <span className="h-4 w-4 rounded border border-slate-300" />
          <span className="text-sm text-slate-800">{t("onboarding.s4.taskLabel")}</span>
        </div>
      </Preview>
      <p className="text-xs text-slate-600">{t("onboarding.s4.info")}</p>
    </div>,

    // Screen 5
    <div key="s5" className="space-y-5" data-testid="onboarding-screen-5">
      <Header title={t("onboarding.s5.title")} />
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-[20px] bg-white border border-border shadow-sm p-4">
          <p className="text-xs uppercase tracking-[0.16em] text-emerald-700 mb-2">{t("onboarding.s5.mayTitle")}</p>
          <ul className="space-y-1.5">
            {t("onboarding.s5.mayItems").map((it) => (
              <li key={it} className="flex items-start gap-2 text-sm text-slate-800"><Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />{it}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-[20px] bg-white border border-border shadow-sm p-4">
          <p className="text-xs uppercase tracking-[0.16em] text-rose-700 mb-2">{t("onboarding.s5.neverTitle")}</p>
          <ul className="space-y-1.5">
            {t("onboarding.s5.neverItems").map((it) => (
              <li key={it} className="flex items-start gap-2 text-sm text-slate-800"><X className="h-4 w-4 text-rose-500 mt-0.5 shrink-0" />{it}</li>
            ))}
          </ul>
        </div>
      </div>
      <p className="text-sm font-medium text-slate-900">{t("onboarding.s5.footer")}</p>
    </div>,
  ];

  const isLast = step === TOTAL - 1;

  return (
    <div className="min-h-screen bg-background transition-colors duration-300 flex flex-col">
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-card/80 border-b border-border">
        <div className="mx-auto max-w-2xl px-5 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {Array.from({ length: TOTAL }).map((_, i) => (
              <span
                key={`onb-dot-${i}`}
                className={[
                  "h-2 rounded-full transition-all",
                  i === step ? "w-5 bg-slate-900" : "w-2 bg-slate-300",
                ].join(" ")}
              />
            ))}
            <span className="ml-2 text-xs font-mono-tab text-slate-500">{t("onboarding.stepOf", { current: step + 1, total: TOTAL })}</span>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <button onClick={exit} className="text-sm text-slate-500 hover:text-slate-800" data-testid="onboarding-skip">
              {t("onboarding.skip")}
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-2xl px-5 py-8">{screens[step]}</main>

      <footer className="sticky bottom-0 bg-card/90 backdrop-blur-xl border-t border-border">
        <div className="mx-auto max-w-2xl px-5 py-3 flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            className="rounded-full text-slate-600 disabled:opacity-40"
            onClick={back}
            disabled={step === 0}
            data-testid="onboarding-back"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />{t("onboarding.back")}
          </Button>
          <Button className="rounded-full px-6" onClick={next} data-testid="onboarding-next">
            {isLast ? t("onboarding.continue") : t("onboarding.next")}
            {!isLast ? <ArrowRight className="h-4 w-4 ml-1.5" /> : null}
          </Button>
        </div>
      </footer>
    </div>
  );
}

function Header({ title, body }) {
  return (
    <div>
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{title}</h1>
      {body ? <p className="mt-3 text-sm text-slate-600 leading-relaxed">{body}</p> : null}
    </div>
  );
}
